import {
  BFF_ROUTES,
  PlanDetailResponseSchema,
  PlanListResponseSchema,
  PricePointsResponseSchema,
  type PlanDetailResponse,
  type PlanListResponse,
} from '@rheora/enrollment-contract';

import {
  isPriceNotPublished,
  type PriceDisclosure,
} from '../services/price-disclosure.service';
import { bffGet, problemTypeOf } from './bff-client';

/**
 * Typed fetchers for the storefront catalogue (E-SF02). Thin: build the path,
 * validate against the shared contract schema, return. No caching, no state —
 * that is the queries layer's job. Pages never import these directly.
 */

/**
 * `GET /bff/plans` — the shelf list, optionally narrowed to ONE delivery utility.
 *
 * `tdu` is the meter's TDU CODE (`MeterDetail.tduCode`), which is the catalogue's
 * own vocabulary and matches `PlanSummary.tdsp` exactly — hyphenated `AEP-C`, not
 * Quotation's underscored `AEP_C`. Passing it makes the BFF narrow BEFORE it
 * prices, which is where the saving is: the price-point reads for the other
 * utilities are never issued. Omitting it asks for the whole state.
 */
export async function fetchPlans(tdu?: string): Promise<PlanListResponse> {
  const path =
    tdu === undefined ? BFF_ROUTES.plans : `${BFF_ROUTES.plans}?tdu=${encodeURIComponent(tdu)}`;
  return bffGet(path, PlanListResponseSchema);
}

/** `GET /bff/plans/:id` — one plan's detail. */
export async function fetchPlanDetail(id: string): Promise<PlanDetailResponse> {
  const path = BFF_ROUTES.planDetail.replace(':id', encodeURIComponent(id));
  return bffGet(path, PlanDetailResponseSchema);
}

/**
 * `GET /bff/plans/:id/price-points` — what we may say about this plan's §25.475
 * average price: the disclosed figures, or a stated refusal to publish one.
 *
 * ── WHY IT RETURNS A UNION AND NOT A THROW ─────────────────────────────────
 * The BFF answers 409 `price-not-published` for a plan whose advertised terms the
 * EFL average-price calculation does not model. That is an ANSWER about the plan
 * — permanent, reasoned, and something the card can explain — so it becomes data.
 * Leaving it an error would put it back in the same bucket as an outage, which is
 * the whole defect: the customer saw "Price unavailable" on a plan an agent had
 * just offered them and could not tell a decision from a broken page.
 *
 * ⚠️ EVERY OTHER FAILURE STILL THROWS. A 404, a 5xx, a shape mismatch, an
 * unreachable BFF — all remain `BffError`, so the card keeps its honest
 * "unavailable" and its retry for the cases where retrying is a real remedy.
 * ⚠️ AND NOTHING IS FABRICATED ON EITHER BRANCH. There is deliberately no
 * fallback to `rateCentsPerKwh` or to plan detail; the refused number does not
 * exist to be substituted.
 */
export async function fetchPriceDisclosure(id: string): Promise<PriceDisclosure> {
  const path = BFF_ROUTES.planPricePoints.replace(':id', encodeURIComponent(id));
  try {
    return { status: 'PUBLISHED', pricePoints: await bffGet(path, PricePointsResponseSchema) };
  } catch (error) {
    if (!isPriceNotPublished(problemTypeOf(error))) throw error;
    // The BFF's public body deliberately carries no machine reason (it is a
    // closed `ProblemDetails` shape), and the type URN is one-to-one with the one
    // reason it is emitted for. Mapping it here keeps the SPA's union total.
    return { status: 'WITHHELD', reason: 'UNMODELLED_PROMOTIONAL_TERM' };
  }
}
